import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { ArrowRight, Clock, GraduationCap, Filter } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { HreflangTags } from '@/components/SEOComponents';

export default function ProgramsPage() {
  const { locale } = useParams();
  const { t } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const [programs, setPrograms] = useState([]);
  const [activeCategory, setActiveCategory] = useState(searchParams.get('type') || null);
  const [loading, setLoading] = useState(true);
  const [cmsCategories, setCmsCategories] = useState([]);

  useEffect(() => {
    setLoading(true);
    const loc = locale || 'en';
    Promise.all([
      api.get(`/programs?locale=${loc}`),
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/site-content/program_categories?locale=${loc}`).then(r => r.json()).catch(() => null),
    ]).then(([progRes, catData]) => {
      setPrograms(progRes.data.programs || []);
      if (catData) {
        const order = catData.meta?.order || Object.keys(catData.content || {});
        const content = catData.content || {};
        setCmsCategories(order.map(key => ({ key, title: content[key]?.title || key, description: content[key]?.description || '' })));
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [locale]);

  const formatPrice = (cents) => `€${(cents / 100).toLocaleString()}`;

  // Filter programs by program_type (CMS categories)
  const filteredPrograms = activeCategory 
    ? programs.filter(p => p.program_type === activeCategory)
    : programs;

  const getCategoryCount = (cat) => programs.filter(p => p.program_type === cat).length;

  const handleCategoryClick = (cat) => {
    const newCat = activeCategory === cat ? null : cat;
    setActiveCategory(newCat);
    if (newCat) {
      setSearchParams({ type: newCat });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div data-testid="programs-page">
      <Helmet><title>{`${t('nav.programs')} — MISEL GROUP MASTERCLASS`}</title></Helmet>
      <HreflangTags path="/programs" />
      
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-end pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1560066984-138dadb4c035?w=1800&q=80" alt="Programs" className="img-editorial" />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 w-full">
          <p className="caption-text animate-fade-up text-gold mb-4">{t('nav.programs')}</p>
          <h1 className="heading-xl animate-fade-up delay-100">{t('home.programs_title')}</h1>
          <p className="body-text mt-4 max-w-2xl animate-fade-up delay-200">{t('home.programs_subtitle')}</p>
        </div>
      </section>

      {/* Category Filter Section */}
      <section className="bg-muted/30 border-b border-border">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-8">
          <div className="flex items-center gap-3 mb-6">
            <Filter size={18} className="text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">{t('programs.filter_by_category')}</span>
          </div>
          
          <div className="flex flex-wrap gap-3">
            {/* All Programs Button */}
            <button
              onClick={() => handleCategoryClick(null)}
              className={`group relative px-5 py-3 rounded-xl text-sm font-medium transition-all duration-300
                ${!activeCategory 
                  ? 'bg-zinc-900 text-white shadow-lg' 
                  : 'bg-white border border-border hover:border-zinc-300 hover:shadow-md'
                }`}
              data-testid="filter-all"
            >
              <span>{t('programs.all_categories')}</span>
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${!activeCategory ? 'bg-white/20' : 'bg-muted'}`}>
                {programs.length}
              </span>
            </button>

            {/* Category Buttons from CMS */}
            {cmsCategories.filter(cat => getCategoryCount(cat.key) > 0).map(cat => {
              const count = getCategoryCount(cat.key);
              const isActive = activeCategory === cat.key;
              
              return (
                <button
                  key={cat.key}
                  onClick={() => handleCategoryClick(cat.key)}
                  className={`group relative px-5 py-3 rounded-xl text-sm font-medium transition-all duration-300
                    ${isActive 
                      ? 'bg-zinc-900 text-white shadow-lg' 
                      : 'bg-white border border-border hover:border-zinc-300 hover:shadow-md'
                    }
                  `}
                  data-testid={`filter-${cat.key}`}
                >
                  <div className="flex flex-col items-start">
                    <span>{cat.title}</span>
                    <span className={`text-xs mt-0.5 ${isActive ? 'text-white/70' : 'text-muted-foreground'}`}>
                      {cat.description}
                    </span>
                  </div>
                  <span className={`absolute -top-2 -right-2 px-2 py-0.5 rounded-full text-xs font-bold
                    ${isActive ? 'bg-gold text-zinc-900' : 'bg-zinc-900 text-white'}
                  `}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Programs List */}
      <section className="section-padding">
        <div className="max-w-[1600px] mx-auto">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" />
            </div>
          ) : filteredPrograms.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-lg text-muted-foreground">{t('programs.no_programs_in_category')}</p>
              <button 
                onClick={() => setActiveCategory(null)} 
                className="mt-4 text-sm text-zinc-900 underline hover:no-underline"
              >
                {t('programs.all_categories')}
              </button>
            </div>
          ) : (
            <div className="space-y-16">
              {filteredPrograms.map((prog, i) => (
                <div 
                  key={prog.id} 
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center animate-fade-up`}
                  style={{ animationDelay: `${i * 100}ms` }}
                  data-testid={`program-row-${prog.slug}`}
                >
                  <div className={i % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="rounded-3xl overflow-hidden shadow-lg relative group">
                      <img 
                        src={prog.hero_media?.url || 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=1200&q=80'} 
                        alt={prog.localization?.title} 
                        className="img-editorial aspect-[4/3] group-hover:scale-105 transition-transform duration-700" 
                      />
                      {/* Category badge on image */}
                      {prog.program_type && (
                        <div className="absolute top-4 left-4 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium">
                          {cmsCategories.find(c => c.key === prog.program_type)?.title || prog.program_type}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    {/* Program type badge */}
                    <p className="caption-text text-gold mb-3">
                      {cmsCategories.find(c => c.key === prog.program_type)?.title || prog.program_type?.toUpperCase()}
                    </p>
                    
                    <h2 className="heading-md mb-4">{prog.localization?.title || prog.slug}</h2>
                    <p className="body-text mb-6">{prog.localization?.intro_statement || ''}</p>
                    
                    <div className="flex flex-wrap gap-6 mb-8">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock size={16} strokeWidth={1.5} /> 
                        {(() => { 
                          const d = prog.duration_display || ''; 
                          const m = d.match(/^(\d+)\s+(.+)$/); 
                          if (!m) return d; 
                          const n = m[1]; 
                          const u = m[2].toLowerCase(); 
                          if (u.includes('month') || u.includes('μήν')) return t('program.duration_months').replace('{count}', n); 
                          if (u.includes('week') || u.includes('εβδομ') || u.includes('ημέρ')) return t('program.duration_weeks').replace('{count}', n); 
                          return d; 
                        })()}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <GraduationCap size={16} strokeWidth={1.5} /> 
                        {prog.max_students ? t('programs.max_students').replace('{count}', prog.max_students) : t('programs.limited_seats')}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 mb-8">
                      <span className="text-2xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                        {formatPrice(prog.tuition_amount)}
                      </span>
                      {prog.payment_plans?.length > 1 && (
                        <span className="text-sm text-muted-foreground">
                          {t('common.or')} {t('common.from')} {formatPrice(prog.payment_plans[1]?.installment_amount || 0)} {t('common.per_month')}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-4">
                      <Link 
                        to={`/${locale || 'en'}/programs/${prog.slug}`} 
                        className="btn-primary" 
                        data-testid={`program-cta-${prog.slug}`}
                      >
                        {t('common.learn_more')} <ArrowRight size={16} className="ml-2" strokeWidth={1.5} />
                      </Link>
                      <Link 
                        to={`/${locale || 'en'}/admissions`} 
                        className="btn-secondary" 
                        data-testid={`program-apply-${prog.slug}`}
                      >
                        {t('common.apply_now')}
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
